// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Organization } from '@/account/api/types';
import {
  CheckCircle,
  ErrorOutlineOutlined,
  HighlightOff,
  Settings,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { HubSpotLogo } from '@/icons/hubspot-logo';
import { findIntegrationConfig } from '@/utilities/integration';
import {
  Box,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

interface HubSpotCRMRowProps {
  organization: Organization;
  isSalesforceConnected: boolean;
  isEnabledForOrg: boolean;
  onOpenIntegrationDialog: () => void;
}

interface SettingsButtonProps {
  onOpen: () => void;
  buttonLabel: string;
  text: string;
}

function SettingsButton(props: SettingsButtonProps) {
  return (
    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        startIcon={<Settings color={'action'} fontSize={'small'} />}
        onClick={props.onOpen}
        variant={'outlined'}
      >
        {props.buttonLabel}
      </Button>
      <Typography sx={{ ml: 1 }}>{props.text}</Typography>
    </Typography>
  );
}

export function HubSpotCRMRow(props: HubSpotCRMRowProps) {
  const crmConfig = findIntegrationConfig(
    props.organization.integrationConfigurations,
    'HUBSPOT'
  );
  const isHubspotConfiguration = crmConfig !== null;

  // Type guard to properly discriminate the HubSpot configuration
  const hubspotConfig = isHubspotConfiguration
    ? crmConfig.configurationState
    : null;

  const isCRMConnected = isHubspotConfiguration;
  const areAllScopesPresent = hubspotConfig?.allScopesPresent ?? false;

  const isCRMConfigured =
    hubspotConfig !== null &&
    hubspotConfig.steps.COMPANY.completed &&
    hubspotConfig.steps.PROJECT.completed &&
    hubspotConfig.steps.PRODUCT.completed;

  const isCRMSyncing = hubspotConfig !== null && isCRMConfigured;
  const crmConfiguredPipelines = hubspotConfig?.configuredPipelines ?? [];
  const hasCRMConfiguredPipelines = crmConfiguredPipelines.length > 0;
  const isCRMFullyConfigured = hasCRMConfiguredPipelines && isCRMConfigured;

  const isDisabledByOtherCRM = props.isSalesforceConnected;
  const isUnavailable = !props.isEnabledForOrg;

  const DisabledCell = (cellProps: { text: string }) => (
    <TableCell>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}
      >
        <HighlightOff color={'disabled'} fontSize={'small'} sx={{ mr: 1 }} />
        {cellProps.text}
      </Typography>
    </TableCell>
  );

  if (isUnavailable) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Contact support to enable this integration" arrow>
              <HubSpotLogo sx={{ width: 100, height: 32, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="HubSpot integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="HubSpot integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="HubSpot integration is not enabled for your organization, contact Hardfin support to activate" />
        <TableCell align={'right'}>{/* Unavailable - no settings */}</TableCell>
      </TableRow>
    );
  }

  if (isDisabledByOtherCRM) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Only one CRM can be connected at a time" arrow>
              <HubSpotLogo sx={{ width: 100, height: 32, opacity: 0.5 }} />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="Not available while Salesforce is already connected" />
        <DisabledCell text="Not available while Salesforce is already connected" />
        <DisabledCell text="Not available while Salesforce is already connected" />
        <TableCell align={'right'}>{/* Disabled - no settings */}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HubSpotLogo sx={{ width: 100, height: 32 }} />
        </Box>
      </TableCell>
      <TableCell>
        {!isCRMConnected ? (
          <SettingsButton
            onOpen={props.onOpenIntegrationDialog}
            buttonLabel="Connect"
            text={'Not connected'}
          />
        ) : areAllScopesPresent ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle color={'success'} fontSize={'small'} sx={{ mr: 1 }} />{' '}
            Connected
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={
                <ErrorOutlineOutlined
                  color={'warning'}
                  fontSize={'small'}
                  sx={{ mr: 1 }}
                />
              }
              onClick={props.onOpenIntegrationDialog}
              variant={'outlined'}
            >
              Partially connected
            </Button>
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isCRMFullyConfigured ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle color={'success'} fontSize={'small'} sx={{ mr: 1 }} />
            Configured
          </Typography>
        ) : isCRMConnected ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={
                <ErrorOutlineOutlined
                  color={'warning'}
                  fontSize={'small'}
                  sx={{ mr: 1 }}
                />
              }
              onClick={props.onOpenIntegrationDialog}
              variant={'outlined'}
            >
              Configure
            </Button>
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not configured
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isCRMSyncing ? (
          crmConfiguredPipelines.map((pipeline) => (
            <Typography
              key={pipeline.pipelineId}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <SyncOutlined
                color={'success'}
                fontSize={'small'}
                sx={{ mr: 1 }}
              />
              {pipeline.pipelineLabel + ' '}
              {pipeline.isEnabled
                ? pipeline.lastSyncedAt !== null
                  ? 'synced at ' +
                    pipeline.lastSyncedAt
                      .setZone(props.organization.timezone)
                      .toLocaleString({
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                  : pipeline.startDate !== null
                    ? 'sync pending'
                    : 'not synced'
                : 'not enabled'}
            </Typography>
          ))
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not syncing
          </Typography>
        )}
      </TableCell>
      <TableCell align={'right'}>
        {isCRMConnected && isCRMConfigured && isCRMSyncing && (
          <Settings onClick={props.onOpenIntegrationDialog} color={'action'} />
        )}
      </TableCell>
    </TableRow>
  );
}
